import { useState, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CloudArrowUp, FilePdf, FileImage, FileDoc, FileXls, X, PaperPlaneTilt, Lightbulb } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_TOTAL_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXT = ["pdf", "png", "jpg", "jpeg", "webp", "docx", "xlsx"];
const ALLOWED_LABEL = "PDF, immagini, Word, Excel";

function fileIcon(f) {
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return <FilePdf size={18} weight="duotone" className="text-red-600" />;
  if (["png", "jpg", "jpeg", "webp"].includes(ext)) return <FileImage size={18} weight="duotone" className="text-blue-600" />;
  if (ext === "docx") return <FileDoc size={18} weight="duotone" className="text-indigo-600" />;
  if (ext === "xlsx") return <FileXls size={18} weight="duotone" className="text-green-600" />;
  return <FilePdf size={18} weight="duotone" className="text-gray-500" />;
}

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n/1024).toFixed(0)} KB`;
  return `${(n/1024/1024).toFixed(2)} MB`;
}

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0; const v = c === "x" ? r : (r & 0x3 | 0x8); return v.toString(16);
  });
}

export default function FeatureProposalForm() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const idemKeyRef = useRef(null);
  const fileInputRef = useRef(null);

  const totalSize = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);
  const overSize = totalSize > MAX_TOTAL_SIZE;
  const overCount = files.length > MAX_FILES;

  const addFiles = (incoming) => {
    const validated = [];
    for (const f of incoming) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`Formato non supportato. Ammessi: ${ALLOWED_LABEL}`); continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`File troppo grande, max 5MB (${f.name})`); continue;
      }
      validated.push(f);
    }
    setFiles(prev => {
      const merged = [...prev, ...validated];
      if (merged.length > MAX_FILES) {
        toast.error(`Massimo ${MAX_FILES} file per proposta`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const canSubmit = title.trim() && description.trim() && !overSize && !overCount && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!idemKeyRef.current) idemKeyRef.current = uuid();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      files.forEach(f => fd.append("files", f));
      const r = await axios.post(`${API}/proposals`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", "Idempotency-Key": idemKeyRef.current },
      });
      if (r.data?.duplicate) {
        toast.success("Proposta già inviata pochi secondi fa, grazie!");
      } else {
        toast.success("Proposta inviata. Grazie!");
      }
      // Reset
      setTitle(""); setDescription(""); setFiles([]); idemKeyRef.current = null;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio proposta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="feature-proposal-form">
      <Card className="p-6 border-[#0D1F3C]/8 bg-gradient-to-br from-blue-50/40 to-white">
        <div className="flex items-start gap-3 mb-2">
          <Lightbulb size={28} weight="duotone" className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-[#0D1F3C]">Proponi una nuova funzionalità</h2>
            <p className="text-sm text-[#0D1F3C]/60 mt-1">Hai un'idea per migliorare Selly? Mandaci una proposta. Le leggiamo tutte.</p>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-[#0D1F3C]/8 space-y-4">
        <div>
          <Label className="text-xs">Titolo *</Label>
          <Input value={title} onChange={e => setTitle(e.target.value.slice(0, 100))}
            placeholder="Es. Esportazione Excel filtrabile delle simulazioni" data-testid="proposal-title" />
          <p className="text-[10px] text-[#0D1F3C]/40 mt-1 text-right">{title.length}/100</p>
        </div>

        <div>
          <Label className="text-xs">Descrizione *</Label>
          <Textarea value={description} onChange={e => setDescription(e.target.value.slice(0, 2000))}
            placeholder="Descrivi la tua idea: cosa risolverebbe, come la useresti, quale workflow miglioreresti..."
            rows={6} data-testid="proposal-description" className="resize-y" />
          <p className="text-[10px] text-[#0D1F3C]/40 mt-1 text-right">{description.length}/2000</p>
        </div>

        <div>
          <Label className="text-xs">Allegati (opzionale)</Label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#1B4FD8] bg-blue-50" : "border-[#0D1F3C]/15 hover:border-[#0D1F3C]/30 bg-white"}`}
            data-testid="proposal-dropzone"
          >
            <CloudArrowUp size={32} weight="duotone" className="mx-auto text-[#0D1F3C]/40 mb-2" />
            <p className="text-sm text-[#0D1F3C]/70"><strong className="text-[#1B4FD8]">Seleziona file</strong> o trascinali qui</p>
            <p className="text-[11px] text-[#0D1F3C]/40 mt-1">{ALLOWED_LABEL} · max 5 file · 5MB ciascuno · 10MB totali</p>
            <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx"
              className="hidden" onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
              data-testid="proposal-file-input" />
          </div>

          {files.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="proposal-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white border border-[#0D1F3C]/10 rounded text-sm">
                  {fileIcon(f)}
                  <span className="flex-1 truncate text-[#0D1F3C]">{f.name}</span>
                  <span className="text-xs text-[#0D1F3C]/50 font-mono">{fmtSize(f.size)}</span>
                  <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-[#0D1F3C]/40 hover:text-red-600" data-testid={`remove-file-${i}`}>
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {files.length > 0 && (
            <p className={`text-[11px] mt-2 font-mono ${overSize || overCount ? "text-red-600 font-semibold" : "text-emerald-700"}`} data-testid="proposal-counter">
              {files.length}/{MAX_FILES} file · {(totalSize/1024/1024).toFixed(2)}/10 MB
              {overSize && " · LIMITE SUPERATO"}
            </p>
          )}
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C] gap-2"
          data-testid="proposal-submit"
        >
          <PaperPlaneTilt size={16} weight="bold" />
          {submitting ? "Invio in corso..." : "Invia proposta"}
        </Button>
      </Card>
    </div>
  );
}
